"use client";

// THE RELAY BAR OF THE ANNEX PAGES — the capsule that comes back once the top bar has scrolled away.
//
// 22/09, Jérémy: « le menu dans la page About doit être similaire à celui esthétiquement de la homepage : centré,
// et fixed quand on scroll. » The homepage has two bars — the one in the hero card, which leaves with the card, and
// the small centred capsule that takes over. The annex pages had only the first: you scrolled, the menu went, and
// nothing ever brought it back. That is the whole complaint, and this file is the answer.
//
// It is NOT a second capsule. The glass, the edge, the arrival, the 44 px zone of the mark are components/fixed-bar.tsx
// and components/fixed-bar.css, which the homepage wears too. What is here is only what the annex pages put inside
// it: their five links (the same components/site-nav-links.ts the top bar reads, so the two can never drift), the
// page they are on, and the phone shape.
//
// Why it is born from components/site-nav.tsx rather than from each page. All eight annex pages already render that
// bar; giving it a client child is how the pill was added (components/site-nav-pill.tsx), and it means no page file
// is touched — /about included — and no page can be forgotten. A bar that had to be added page by page would be
// missing from the ninth.
//
// Why it renders nothing until it is mounted. Without JavaScript this capsule can never arm: it has no scroll to
// read, so it would be a dead copy of the menu sitting in the static HTML — a second nav landmark, a second
// aria-current, every destination written twice for a crawler and a screen reader. So the server and the first
// client render agree on nothing at all, and the capsule appears only once the script is running, which is exactly
// when it can work. Without a script the top bar stays the navigation, untouched.

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import FixedBar from "./fixed-bar";
import NavMenu from "./nav-menu";
import SiteNavPill from "./site-nav-pill";
import { SITE_NAV_LINKS, type SiteNavCurrent } from "./site-nav-links";

const ASSETS = "/assets/v2/top";

/**
 * The row inside the capsule: the homepage capsule's own recipe (.v2-nav--fixed, components/fixed-bar.css), with
 * this site's five links instead of the four hero anchors. Copying the homepage's list would have been a bug, not a
 * shortcut: #why and #northos are sections of the homepage and do not exist on /about.
 * Exported so a test can render it on its own — the capsule around it only exists in a browser.
 */
export function SiteFixedNavRow({ current, shown }: { current?: SiteNavCurrent; shown?: boolean }) {
  // The top bar is off screen when the capsule is up. Leaving it in the tab order would give a keyboard two
  // "Primary" landmarks and two current pages at once, and send the reader into a bar nobody can see. The homepage
  // does the same thing declaratively (home-v2/nav.tsx, inert on the card nav); here the bar is a server component
  // in another subtree, so the capsule marks it from the DOM. It is put back the moment the capsule leaves.
  useEffect(() => {
    const head = document.querySelector(".v2-sitenav");
    if (!head) return;
    head.toggleAttribute("inert", Boolean(shown));
    return () => head.removeAttribute("inert");
  }, [shown]);

  return (
    <nav className="v2-nav v2-nav--fixed" aria-label="Primary">
      <ul className="v2-nav__list">
        {SITE_NAV_LINKS.map((link, i) => (
          <li key={link.href} className="v2-nav__item">
            {/* The homepage's gesture, fed by the page instead of by the section on screen: the pill rests on the
                item marked aria-current, flies to whatever is hovered, comes back on leave. Same component as the
                top bar's, pointed at this row's class names. */}
            {i === 0 && (
              <SiteNavPill listSelector=".v2-nav__list" linkSelector=".v2-nav__link" className="v2-nav__pill ds-edge" />
            )}
            <Link
              className={`v2-nav__link${i === 0 ? " v2-nav__link--lead" : ""}`}
              href={link.href}
              aria-current={link.href === current ? "page" : undefined}
            >
              <span className="v2-nav__label">{link.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function SiteFixedNav({ current }: { current?: SiteNavCurrent }) {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  if (!ready) return null;

  return (
    <FixedBar
      variant="v2-fixednav--site"
      // The top bar itself is the anchor: the capsule arrives exactly when the bar one was reading has gone. It is
      // the mirror of the homepage, which watches the card ITS top bar lives in, it needs no extra element on the
      // page, and it covers the eight annex pages by construction since every one of them renders that bar.
      anchor={(bar) => bar.parentElement?.querySelector(".v2-sitenav") ?? document.querySelector(".v2-sitenav")}
      logo={
        <Link className="v2-fixednav__logo" href="/" aria-label="North home">
          {/* The two SVG files the top bar of these same pages already draws, so a page's two bars show one mark. */}
          <span className="v2-fixednav__logoart" role="img" aria-label="North">
            <Image className="v2-fixednav__mark" src={`${ASSETS}/north-mark-nav.svg`} alt="" width={32} height={32} />
            <Image className="v2-fixednav__word" src={`${ASSETS}/north-wordmark.svg`} alt="" width={56} height={10} />
          </span>
        </Link>
      }
    >
      {(shown) => (
        <>
          {/* Under 780 px the five items do not fit the capsule — measured, see fixed-bar.css — so it takes the
              shape its own top bar takes at that width. Literally the same shape: this is the top bar's button,
              the same component and the same sheet (components/nav-menu.tsx), wearing this bar's class family. */}
          <NavMenu block="v2-fixednav" />
          {/* display: contents above 780 px — the nav is the flex item it would have been. Below, it is the panel. */}
          <div className="v2-fixednav__panel">
            <SiteFixedNavRow current={current} shown={shown} />
          </div>
        </>
      )}
    </FixedBar>
  );
}
